import { oc } from "@orpc/contract";
import { z } from "zod";

export const SendComposeReplyInputSchema = z.object({
  /** Sales-order draft the reply is composed from (for threading + sender). */
  salesOrderDraftId: z.string().uuid().optional(),
  to: z.array(z.string().email()).min(1),
  cc: z.array(z.string().email()).optional(),
  bcc: z.array(z.string().email()).optional(),
  subject: z.string().min(1),
  htmlContent: z.string().min(1),
  /** Explicit Send-From mailbox override (authorized server-side). */
  mailboxUpn: z.string().email().optional(),
});

export const SendComposeReplyOutputSchema = z.object({
  outboundEmailId: z.string().uuid(),
  messageId: z.string().nullable(),
  conversationId: z.string().optional(),
});

export const sendComposeReplyContract = oc
  .input(SendComposeReplyInputSchema)
  .output(SendComposeReplyOutputSchema)
  .route({ method: "POST" });

// Mailboxes the current user may send from (org ingest mailboxes), plus the
// org's configured default send UPN (if it's among them). Empty when the user
// lacks the SharedMailboxSend permission.
export const SendableMailboxesOutputSchema = z.object({
  mailboxes: z.array(
    z.object({ upn: z.string(), name: z.string().nullable() }),
  ),
  defaultUpn: z.string().nullable(),
});

export const sendableMailboxesContract = oc
  .output(SendableMailboxesOutputSchema)
  .route({ method: "GET" });

const DefaultSendMailboxSchema = z
  .object({
    mailboxId: z.string().uuid(),
    upn: z.string(),
    name: z.string().nullable(),
  })
  .nullable();

export const getDefaultSendContract = oc
  .output(DefaultSendMailboxSchema)
  .route({ method: "GET" });

export const SetDefaultSendInputSchema = z.object({
  emailIngestMailboxId: z.string().uuid().nullable(),
});

export const setDefaultSendContract = oc
  .input(SetDefaultSendInputSchema)
  .output(DefaultSendMailboxSchema)
  .route({ method: "POST" });

export const composeReplyContracts = {
  send: sendComposeReplyContract,
  sendableMailboxes: sendableMailboxesContract,
  defaultSend: {
    get: getDefaultSendContract,
    set: setDefaultSendContract,
  },
} as const;

export type SendComposeReplyInput = z.infer<typeof SendComposeReplyInputSchema>;
export type SendComposeReplyOutput = z.infer<
  typeof SendComposeReplyOutputSchema
>;
export type SendableMailboxesOutput = z.infer<
  typeof SendableMailboxesOutputSchema
>;
export type SetDefaultSendInput = z.infer<typeof SetDefaultSendInputSchema>;
